import * as Yup from "yup";
import { useState } from "react";
import { Box, Button, Center, Checkbox, Flex, FormControl, FormErrorMessage, FormLabel, Image, Input, InputGroup, InputRightElement, Radio, RadioGroup, Text, useToast, VStack, WrapItem } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { BiHide, BiShow } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";

import { useAuth } from "../../features/admin/auth/hooks";
import { useApiClient } from "../../shared/hooks";

interface ILogin {
  email: string;
  password: string;
  rememberMe?: boolean;
}

function LoginPage() {
  const AdminLoginSchema = Yup.object().shape({
    email: Yup.string()
      .email("Email is required.")
      .required("Email is required."),
  
    password: Yup.string().required("Password is required"),
  });

  const { setUser, setRememberMe, rememberMe } = useAuth();

  const [rememberMeData] = useState<any>(rememberMe);
  const toast = useToast();
  const navigate = useNavigate();
  const [show, setShow] = useState<boolean>(false);
  const handleClick = () => {
    setShow(!show);
  };
  const apiClient = useApiClient();

  const [env, setEnv] = useState(localStorage.getItem('backend_env') || 'prod');

  const handleEnvChange = (value: string) => {
    setEnv(value);
    localStorage.setItem('backend_env', value);
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ILogin>({
    resolver: yupResolver(AdminLoginSchema),
    defaultValues: {
      email: rememberMeData?.email || "",
      password: rememberMeData?.password || "",
    },
  });

  const loginFn = async (data: ILogin) => {
    console.log("Login function started", data);
    const response = await apiClient.signIn(data.email, data.password);
    console.log("Login response:", response);

    let userDetail;
    let modifyData: any;
    if (response?.status) {
      localStorage.setItem("user", JSON.stringify(response.data));
      console.log("User data stored in localStorage", response.data);
      userDetail = await apiClient.getProfileRole(response.data.access_token);
      console.log("User detail response:", userDetail);

      if (!userDetail?.status) {
        console.error("Failed to retrieve user details");
      } else {
        modifyData = {
          access_token: response?.data?.access_token,
          refresh_token: response?.data?.refresh_token,
          expires_in: response?.data?.expires_in,
          refresh_expires_in: response?.data?.refresh_expires_in,
          user_id: response?.data?.user_id,
          email: userDetail.data?.email,
          user_name: userDetail.data.user_name,
          user_profile_image: userDetail?.data?.user_profile_image,
          full_name: userDetail.data.full_name,
          role: userDetail.data.role,
          is_email_verified: true,
          disabled: false,
        };
        console.log("Modified data:", modifyData);
      }
    }

    if (response?.status && userDetail?.status && userDetail.data.role) {
      setUser(modifyData);
      localStorage.setItem("user", JSON.stringify(modifyData));
      console.log("User data updated and stored in localStorage", modifyData);
    } else {
      console.error(
        "Invalid login credential or user detail missing/incorrect"
      );
      throw new Error(
        "Invalid login credential or user detail missing/incorrect"
      );
    }

    if (!response?.status) {
      console.error("Login attempt failed due to invalid credentials");
      throw new Error("Invalid login credential");
    }
  };

  const mutation = useMutation({
    mutationFn: loginFn,
    onSuccess: () => {
      navigate("/admin");
    },
  });

  const onSubmit = async (loginInput: ILogin) => {
    try {
      if (loginInput.rememberMe) {
        const encryptedRememberMe = {
          email: loginInput?.email,
          password: loginInput?.password,
        };
        // setRememberMe(encryptedRememberMe)
        localStorage.setItem(
          "userRememberMe",
          JSON.stringify(encryptedRememberMe)
        );
      } else {
        setRememberMe("{}");
      }
      await mutation.mutateAsync({
        email: loginInput?.email,
        password: loginInput?.password,
      });
      navigate("/admin", {
        replace: true,
      });
      toast.closeAll();
      toast({
        title: "Success",
        description: "Login Successfully",
        status: "success",
        isClosable: true,
        duration: 2000,
      });
    } catch (err: any) {
      toast.closeAll();
      toast({
        title: "Error",
        description:"Invalid login credential",
        status: "error",
        isClosable: true,
        duration: 2000,
      });
    }
  };

  return (
    <Box
      height={"100vh"}
      display="flex"
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Box
        border={` 1px solid #d3d3d3`}
        p={{ base: 2, md: 10, lg: "40px" }}
        borderRadius={10}
      >
        <Box
          gap={5}
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          mt={0}
          mx={"auto"}
        >
          <Box
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
            gap={10}
            ml={{ base: 5, md: 0, lg: 10 }}
          >
            <Center>
              <Image
                cursor={"pointer"}
                height={"130px"}
                w={"130px"}
                objectFit="cover"
                src={"https://i.ibb.co/kBKkcjp/going-solo-logo.png"}
                alt="logo"
              />
            </Center>
            <Box
              textAlign={"center"}
              width={{ base: "auto", md: "auto", lg: "400px" }}
            >
              <Text
                fontSize={"2rem"}
                color="#005050"
                fontWeight={600}
              >
                Going Solo
              </Text>
            </Box>
          </Box>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box width={"100%"} mt={10}>
            <VStack>
              <FormControl pt="3" isInvalid={errors.email != null}>
                <FormLabel>Email</FormLabel>
                <Input
                  size="sm"
                  variant={errors?.email != null ? "error" : ""}
                  type="text"
                  {...register("email")}
                  placeholder="Enter your email"
                />
                <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
              </FormControl>
              <FormControl pt="2" mb="5" isInvalid={errors.password != null}>
                <FormLabel>Password</FormLabel>
                <InputGroup size="sm">
                  <Input
                    variant={errors?.password != null ? "error" : ""}
                    type={show ? "text" : "password"}
                    {...register("password")}
                    placeholder={"xxxxxxxx"}
                  />
                  <InputRightElement onClick={handleClick} cursor="pointer">
                    {show ? <BiShow /> : <BiHide />}
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>
                  {errors?.password?.message}
                </FormErrorMessage>
              </FormControl>
            </VStack>
            {/* Environment Selector */}
            <FormControl as="fieldset" pt="3">
              <RadioGroup onChange={handleEnvChange} value={env}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                  <Radio value="prod">Production</Radio>
                  <Radio value="stag">Staging</Radio>
                  <Radio value="dev">Development</Radio>
                  <Radio value="local">Local</Radio>
                </div>
              </RadioGroup>
            </FormControl>
            <Flex my="4" alignItems="center">
              <Checkbox {...register("rememberMe")} defaultChecked>
                Remember Password
              </Checkbox>
            </Flex>
            <WrapItem pt="2" width={"100%"}>
              <Button
                isLoading={isSubmitting}
                loadingText="Loading..."
                size="sm"
                type="submit"
                width={"100%"}
                variant={"primary"}
              >
                Login
              </Button>
            </WrapItem>
          </Box>
        </form>
      </Box>
    </Box>
  );
}

export default LoginPage;
